import { useCallback, useEffect, useRef, useState } from "react";
import { Empty, Input, Modal, Spin, Typography } from "antd";
import { SearchOutlined, HistoryOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { useVisitHistory } from "../context/HistoryContext";
import { hasAnyGrant } from "../helpers/permissions";
import { searchTarget } from "../helpers/searchTargets";
import { useGlobalSearch, type PaletteHit } from "../helpers/useGlobalSearch";
import { SearchHit } from "./SearchHit";

/**
 * The command palette: one box that reaches every page, report, document and master record
 * the user can see. Opened from the header button or by pressing "/" anywhere.
 *
 * Navigation here is card-driven — landing page, module hub, register — which is fine when
 * you know which of the forty registers owns what you are after, and useless when you only
 * know a doc number. This is the shortcut across that.
 */

/** Whether a keystroke landed somewhere the user is actually typing. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const { user } = useUser();
  const navigate = useNavigate();
  const { entries } = useVisitHistory();
  const { term, setTerm, groups, flat, loading, fuzzy } = useGlobalSearch(user);
  const listRef = useRef<HTMLDivElement>(null);

  // "/" is the shortcut, and Ctrl/Cmd+K is the one people reach for first — both open the
  // same box. Neither fires while the user is typing into a field, and "/" is swallowed so
  // it does not also land in the input it just opened.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const palette = e.key === "k" && (e.metaKey || e.ctrlKey);
      const slash = e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey;
      if (!palette && !slash) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      setOpen(true);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // A new query invalidates wherever the highlight was sitting.
  useEffect(() => setActive(0), [term]);

  const close = useCallback(() => {
    setOpen(false);
    setTerm("");
    setActive(0);
  }, [setTerm]);

  const go = useCallback(
    (path: string) => {
      close();
      navigate(path);
    },
    [close, navigate],
  );

  /** Where a hit opens, or null when the user cannot open it. */
  const pathFor = useCallback(
    (hit: PaletteHit): string | null => {
      if (hit.source === "local") return hit.path;
      const target = searchTarget(hit.hit);
      // The server has already filtered to what this user can read, so this is a backstop —
      // the same one DocLink and the approvals bell apply.
      return target && hasAnyGrant(user, target.permission) ? target.path : null;
    },
    [user],
  );

  function onInputKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (flat.length === 0) return;
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((prev) => (prev + step + flat.length) % flat.length);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const hit = flat[active];
      const path = hit && pathFor(hit);
      // Nothing highlighted, or a hit with nowhere to go: fall through to the full results
      // page, which is the right answer for a broad query anyway.
      if (path) go(path);
      else if (term.trim()) go(`/search?q=${encodeURIComponent(term.trim())}`);
    }
  }

  // Keep the highlighted row in view when the arrows walk past the fold.
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const recent = entries.slice(0, 5);
  // Rows carry their position in `flat`, since that is the one list the arrow keys walk.
  // The offset each group starts at is derived here rather than by advancing a counter
  // while the rows render: a variable reassigned mid-render is exactly what React forbids.
  const groupStart = groups.map((_, gi) => groups.slice(0, gi).reduce((n, g) => n + g.hits.length, 0));

  return (
    <>
      {/* A box that looks like the search field it opens, rather than an icon the user has
          to guess at — and it carries the shortcut, so "/" is discoverable without being
          documented anywhere. It is a button, not an input: the real field is in the modal,
          and two of them would mean typing into one and watching the other fill in. */}
      <button type="button" className="glut-search-trigger" aria-label="Search" onClick={() => setOpen(true)} data-tour="global-search">
        <SearchOutlined />
        <span className="glut-search-trigger-text">Type / to search</span>
      </button>

      <Modal
        open={open}
        onCancel={close}
        footer={null}
        closable={false}
        destroyOnHidden
        width={640}
        style={{ top: 80 }}
        styles={{ body: { padding: 0 } }}
        aria-label="Search"
      >
        <Input
          autoFocus
          variant="borderless"
          size="large"
          prefix={<SearchOutlined />}
          suffix={loading ? <Spin size="small" /> : null}
          placeholder="Search pages, documents, items..."
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={onInputKeyDown}
        />

        <div className="glut-search-results" ref={listRef}>
          {!term.trim() &&
            (recent.length > 0 ? (
              <div className="glut-search-group">
                <div className="glut-search-heading">
                  <HistoryOutlined /> Recent
                </div>
                {recent.map((entry) => (
                  <div key={entry.key} className="glut-search-row" onClick={() => go(entry.path)}>
                    {entry.label}
                  </div>
                ))}
              </div>
            ) : (
              <Typography.Text type="secondary" className="glut-search-placeholder">
                Search for a page, a document number, an item code or a name.
              </Typography.Text>
            ))}

          {term.trim() && groups.length === 0 && !loading && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`Nothing matches "${term.trim()}"`} />
          )}

          {/* Say so, rather than quietly answering a different question than the one asked:
              nothing matched what was typed, and these are the nearest names to it. */}
          {fuzzy && (
            <div className="glut-search-note">
              No exact matches for "{term.trim()}" - showing similar names
            </div>
          )}

          {groups.map((group, gi) => (
            <div key={group.key} className="glut-search-group">
              <div className="glut-search-heading">{group.label}</div>
              {group.hits.map((hit, i) => {
                const at = groupStart[gi] + i;
                const path = pathFor(hit);
                return (
                  <div
                    key={hit.key}
                    className={`glut-search-row${path ? "" : " glut-search-row-inert"}`}
                    data-active={at === active}
                    onMouseEnter={() => setActive(at)}
                    onClick={() => path && go(path)}
                  >
                    <SearchHit hit={hit} term={term} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="glut-search-hint">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> navigate
          </span>
          <span>
            <kbd>↵</kbd> open
          </span>
          <span>
            <kbd>esc</kbd> close
          </span>
        </div>
      </Modal>
    </>
  );
}
